import React, { useContext, useState } from "react";
import { Flex } from "@acko-tech/building-blocks.ui.common";
import {
  CoverageTable,
  CoverageItem,
  CoverageTag,
  Row,
  Heading,
  DiscountTag,
  Desc
} from "./styles";
import DOMPurify from "isomorphic-dompurify";
import { Context } from "@components/useContext/context";
import Hyperlink from "@components/common/hyperlink";
import { resolvePath } from "@utils/resolvePath";
import { getInrFormat } from "@utils/inrFormatter";

const PA_OWNER_CAR = "pa_owner_car";
const RSA_COMBO_CAR = "rsa_outstation_keyprotect_combo_car";
const COMPARISON_METADATA_KEYS = new Set([
  "showInfoIcon",
  "discount",
  "tag",
  "addon_id",
  "offer_type",
  "current_sum_insured",
  "upgraded_sum_insured"
]);

interface StructuredComparisonValue {
  label: string;
  value: string;
}

interface FreeUpgradePresentation {
  tag: string;
  previous?: boolean | StructuredComparisonValue;
  current?: boolean | StructuredComparisonValue;
}

const formatSumInsured = (amount?: number | string | null) => {
  const numericAmount = Number(amount);
  if (!Number.isFinite(numericAmount) || numericAmount <= 0) {
    return "";
  }
  if (numericAmount >= 100000) {
    return `₹${numericAmount / 100000} Lakh`;
  }
  return `₹${getInrFormat(numericAmount)}`;
};

const sumInsuredValue = (
  amount?: number | string | null
): StructuredComparisonValue | undefined => {
  const value = formatSumInsured(amount);
  return value ? { label: "Sum Insured", value } : undefined;
};

export const getFreeUpgradeComparisonPresentation = (
  coverItem: any
): FreeUpgradePresentation | null => {
  if (coverItem?.offer_type === "addon_upgrade") {
    if (coverItem?.addon_id === RSA_COMBO_CAR) {
      return {
        tag: "Upgraded at no extra cost",
        previous: { label: "Free Towing :", value: "40 km" },
        current: { label: "Free Towing :", value: "Unlimited kms" }
      };
    }

    if (coverItem?.addon_id === PA_OWNER_CAR) {
      return {
        tag: "Upgraded at no extra cost",
        previous:
          sumInsuredValue(coverItem.current_sum_insured) ??
          coverItem.previous,
        current:
          sumInsuredValue(coverItem.upgraded_sum_insured) ??
          coverItem.current
      };
    }
  }

  if (
    coverItem?.offer_type === "free_addon" &&
    coverItem?.addon_id === PA_OWNER_CAR
  ) {
    return {
      tag: "Added for FREE",
      previous: false,
      current:
        sumInsuredValue(coverItem.current_sum_insured) ?? coverItem.current
    };
  }

  return null;
};

const isStructuredComparisonValue = (
  value: unknown
): value is StructuredComparisonValue =>
  Boolean(
    value &&
      typeof value === "object" &&
      "label" in value &&
      "value" in value
  );

const renderStructuredComparisonValue = (
  value: StructuredComparisonValue
) => (
  <div>
    <div>{value.label}</div>
    <div>{value.value}</div>
  </div>
);

const CoverageComparison = ({ children, ...props }) => {
  const { heading, desc, covers, actions, isBundledODFlow, eligibilityReason, addonNotEligible } = props || {};
  const {
    on_click,
    on_click_show_info_idv,
    on_click_show_info_ncb,
    on_click_show_info_primary_covers,
    on_click_show_info_additional_covers
  } = actions || {};
  const { isDesktop } = useContext(Context);

  const trigger = (actionArray) => {
    actionArray?.forEach(
      (eachFunc) =>
        typeof eachFunc.funCall === "function" &&
        eachFunc.funCall(eachFunc.args)
    );
  };

  const handleClick = (rawValue) => {
    const plainText = rawValue?.replace(/<[^>]*>?/gm, "").trim();

    switch (plainText) {
      case "Insured Value (IDV)":
        trigger(on_click_show_info_idv);
        break;
      case "No claim bonus (NCB)":
        trigger(on_click_show_info_ncb);
        break;
      case "Primary Covers":
        trigger(on_click_show_info_primary_covers);
        break;
      case "Additional Covers":
        trigger(on_click_show_info_additional_covers);
        break;
      default:
        console.warn("No matching click handler for:", plainText);
    }
  };

  const shouldShowHeading =
  Array.isArray(addonNotEligible) && addonNotEligible.length > 0 ||
  eligibilityReason !== "EligibilityReasonNotApplicable";

  const isNetPremiumRow = (coverItem) => {
    const rawKey = String(coverItem?.key || "");
    const plainText = rawKey.replace(/<[^>]*>?/gm, "").trim();
    return plainText === "Net Premium";
  };

  return (
    <Flex
      style={{
        justifyContent: "center",
        flexDirection: "column"
      }}
    >
      {shouldShowHeading && (
          <Heading
            dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(heading) }}
          />
        )}
      {isBundledODFlow === true && (
        <Desc>
          <span
            style={{
              fontSize: isDesktop ? "14px" : "12px"
            }}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(desc)
            }}
          />
          &nbsp;
          <Hyperlink
            isDesktop={isDesktop}
            clickHandler={() => {
              if (on_click?.length > 0) {
                on_click.forEach((eachFunc) => {
                  typeof eachFunc.funCall === "function" &&
                    eachFunc.funCall(eachFunc.args);
                });
              }
            }}
            type="UNDERLINE"
            is_visible={true}
            text="Learn more"
            styles={{
              display: "inline",
              fontSize: isDesktop ? "14px" : "12px",
              lineHeight: "20px",
              color: "#2E2E38"
            }}
          />
        </Desc>
      )}
      <CoverageTable>
        <Row>
          <CoverageItem isTitle={true} isFeature={true}>
            <div>Coverage details</div>
          </CoverageItem>
          <CoverageItem isTitle={true} isFeature={false}>
            <div>Last year</div>
          </CoverageItem>
          <CoverageItem
            isTitle={true}
            isFeature={false}
            background="rgba(253, 244, 251, 1)"
          >
            <div id="thisYear">This year</div>
          </CoverageItem>
        </Row>
        {covers.map((coverItem, itemIdx) => {
          const freeUpgradePresentation =
            getFreeUpgradeComparisonPresentation(coverItem);
          const displayCoverItem = freeUpgradePresentation
            ? {
                ...coverItem,
                tag: freeUpgradePresentation.tag,
                previous:
                  freeUpgradePresentation.previous ?? coverItem.previous,
                current: freeUpgradePresentation.current ?? coverItem.current
              }
            : coverItem;

          return (
            <Row
              key={itemIdx}
              className={
                isNetPremiumRow(displayCoverItem) ? "premium-row" : undefined
              }
            >
              {Object.entries(displayCoverItem).map(([key, value], idx) => {
                if (COMPARISON_METADATA_KEYS.has(key)) return null;

                const isIcon = value === true || value === false;
                const iconSrc =
                  value === true
                    ? resolvePath("/images/tick_icon.svg")
                    : resolvePath("/images/cross.svg");

                if (key === "current" && displayCoverItem.discount) {
                  return (
                    <CoverageItem
                      key={idx}
                      isFeature={idx === 0}
                      style={{ position: "relative" }}
                    >
                      <DiscountTag isDesktop={isDesktop}>
                        -₹{displayCoverItem.discount} less
                      </DiscountTag>
                      <div
                        dangerouslySetInnerHTML={{
                          __html: DOMPurify.sanitize(displayCoverItem.current)
                        }}
                      />
                    </CoverageItem>
                  );
                }

                return (
                  <CoverageItem
                    key={idx}
                    background={idx === 2 && "rgba(253, 244, 251, 1)"}
                    isFeature={idx === 0}
                  >
                    <div
                      style={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center"
                      }}
                    >
                      {key === "key" ? (
                        <div
                          style={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "flex-start",
                            gap: "4px"
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              alignItems: "center"
                            }}
                          >
                            <div
                              dangerouslySetInnerHTML={{
                                __html: DOMPurify.sanitize(String(value))
                              }}
                            />
                            {coverItem.showInfoIcon === true && (
                              <img
                                src={resolvePath("/images/Info.svg")}
                                alt="Info"
                                style={{
                                  marginLeft: "8px",
                                  width: "16px",
                                  height: "16px",
                                  cursor: "pointer"
                                }}
                                onClick={() => handleClick(value)}
                              />
                            )}
                          </div>
                          {displayCoverItem.tag ? (
                            <CoverageTag>{displayCoverItem.tag}</CoverageTag>
                          ) : null}
                        </div>
                      ) : isIcon ? (
                        <img
                          src={iconSrc}
                          alt={value ? "Tick" : "Cross"}
                          style={{ width: "16px", height: "16px" }}
                        />
                      ) : isStructuredComparisonValue(value) ? (
                        renderStructuredComparisonValue(value)
                      ) : (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: DOMPurify.sanitize(String(value))
                          }}
                        />
                      )}
                    </div>
                  </CoverageItem>
                );
              })}
            </Row>
          );
        })}
      </CoverageTable>
    </Flex>
  );
};

export default CoverageComparison;
